// =============================================================
//  waveform.js -- SVG waveform display for audio cues
//
//  Renders amplitude contour into a composer-placed <rect> in the
//  score SVG.  Two black polylines (upper/lower peak envelope),
//  transparent fill, thin cursor line.  Info label as SVG text
//  at the bottom of the display area.
//
//  Usage from audio cue DSL:
//    audio(src:drone, waveform:self)       -- use cue element
//    audio(src:drone, waveform:wf_drone)   -- use named rect
//    audio(src:drone, waveform:none)       -- suppress waveform
//
//  "self" is the default -- waveform renders into the cue element.
//
//  Exports:
//    renderWaveform(svg, target, buffer, uid, filename, opts)
//    startCursor(handle, audioCtx, startTime, duration, speed, loop)
//    resetCursor(handle)
//    addCursor(handle, cursorId, opts)    -- multi-cursor
//    removeCursor(handle, cursorId)       -- multi-cursor
//    removeAllCursors(handle)             -- multi-cursor
//    updatePeaks(handle, buffer, filename) -- pool file switching
//    destroyWaveform(uid)
//    getWaveform(uid)
// =============================================================
import { applyVizLine } from "./vizStyle.js";

const SVG_NS = "http://www.w3.org/2000/svg";

// Global label visibility state (default: hidden)
window.waveformLabelsVisible = false;

// Cache: filename -> { maxPeaks, minPeaks }
const peakCache = new Map();

// =============================================================
//  Label visibility toggle functions
// =============================================================

/**
 * Toggle label details visibility for a single waveform.
 * Header (type + label) is always visible; this toggles the details (filename + params).
 * @param {object} handle - waveform handle
 * @param {boolean} [visible] - explicit state (optional, toggles if omitted)
 */
export function toggleWaveformLabel(handle, visible) {
  if (!handle?._fo) return;

  const newState = visible !== undefined ? visible : !handle._labelVisible;
  handle._labelVisible = newState;

  // Show/hide just the details div (header stays visible)
  if (handle._detailsDiv) {
    handle._detailsDiv.style.display = newState ? "" : "none";
  }

  // Recalculate layout when showing/hiding details
  updateWaveformLayout(handle);
}

/**
 * Set label visibility for ALL active waveforms.
 * @param {boolean} visible - true to show, false to hide
 */
export function setAllWaveformLabelsVisible(visible) {
  window.waveformLabelsVisible = visible;

  for (const [, handle] of activeWaveforms) {
    toggleWaveformLabel(handle, visible);
  }

  // Update global toggle button state
  const globalBtn = document.getElementById("waveform-labels-toggle");
  if (globalBtn) {
    globalBtn.classList.toggle("active", visible);
  }
}

/**
 * The label's height in the box's own units: the foreignObject's layout px
 * (offsetHeight), which the score's zoom does not scale -- a screen
 * measure (getBoundingClientRect) put the peaks under half the label at
 * 50 % and under a gap twice its size at 200 %. Before it is laid out, an
 * estimate from its text.
 */
function labelHeight(div, bbox, fontSize) {
  const lineHeight = fontSize * 1.2;
  let h = div.offsetHeight || div.scrollHeight;
  if (!(h > 0)) {
    // Min 1 char/line -- tiny elements (e.g. a 10px rect) otherwise divide by
    // zero here and Infinity poisons every height/y attribute downstream
    const charsPerLine = Math.max(1, Math.floor(bbox.width / (fontSize * 0.6)));
    h = Math.ceil(((div.textContent || "").length || 1) / charsPerLine) * lineHeight + 4;
  }
  return Number.isFinite(h) && h > 0 ? h : lineHeight + 4;
}

/** The room the label and its gap take at the top -- never the whole box, so the draw area stays >= 0. */
const labelPad = (height, bbox, gap) => Math.min(height + gap, Math.max(0, bbox.height - 4));

/** The peaks (the base contour and every layer) drawn into `d`, at its width. */
function drawPeaks(handle, d) {
  const buckets = Math.min(Math.round(d.width), 2000);
  const draw = (upper, lower, peaks) => {
    upper.setAttribute("points", peaksToPoints(peaks.maxPeaks, d.x, d.y, d.width, d.height));
    lower.setAttribute("points", peaksToPoints(peaks.minPeaks, d.x, d.y, d.width, d.height));
  };
  if (handle.upperLine && handle.lowerLine) {
    const peaks = handle._buffer && handle._filename ? getPeaks(handle._filename, handle._buffer, buckets) : handle._peaks;
    if (peaks) draw(handle.upperLine, handle.lowerLine, peaks);
  }
  for (const [, layer] of handle._peakLayers || []) {
    if (layer.upper && layer.lower && layer.buffer) draw(layer.upper, layer.lower, getPeaks(layer.filename, layer.buffer, buckets));
  }
}

// Active waveform handles: uid -> handle object
const activeWaveforms = new Map();
// Persists cursor params across destroyWaveform/recreate cycles so re-renders
// can restart the cursor animation for voices that are still playing.
// Keyed by waveform uid. Cleared by clearCursorParams() when audio stops.
const _cursorRestoreParams = new Map();

// =============================================================
//  Peak extraction (normalised)
// =============================================================

/**
 * Downsample an AudioBuffer into normalised peak envelopes.
 * Peaks are scaled so the loudest sample fills the full
 * display height -- quiet files won't waste space.
 */
export function extractPeaks(buffer, buckets, { normalize = true } = {}) {
  const channels = buffer.numberOfChannels;
  const length   = buffer.length;
  const step     = Math.max(1, Math.floor(length / buckets));

  const maxPeaks = new Float32Array(buckets);
  const minPeaks = new Float32Array(buckets);

  maxPeaks.fill(0);
  minPeaks.fill(0);

  for (let ch = 0; ch < channels; ch++) {
    const data = buffer.getChannelData(ch);

    for (let b = 0; b < buckets; b++) {
      const start = b * step;
      const end   = Math.min(start + step, length);

      let bMax = -1, bMin = 1;
      for (let i = start; i < end; i++) {
        if (data[i] > bMax) bMax = data[i];
        if (data[i] < bMin) bMin = data[i];
      }

      if (ch === 0) {
        maxPeaks[b] = bMax;
        minPeaks[b] = bMin;
      } else {
        maxPeaks[b] = Math.max(maxPeaks[b], bMax);
        minPeaks[b] = Math.min(minPeaks[b], bMin);
      }
    }
  }

  // Normalise: scale so loudest peak = 1.0
  let absMax = 0;
  for (let b = 0; b < buckets; b++) {
    const a = Math.abs(maxPeaks[b]);
    const n = Math.abs(minPeaks[b]);
    if (a > absMax) absMax = a;
    if (n > absMax) absMax = n;
  }

  if (normalize && absMax > 0 && absMax < 1) {
    const scale = 1 / absMax;
    for (let b = 0; b < buckets; b++) {
      maxPeaks[b] *= scale;
      minPeaks[b] *= scale;
    }
  }

  return { maxPeaks, minPeaks };
}

/**
 * Get or compute cached peaks for a filename.
 */
export function getPeaks(filename, buffer, buckets) {
  const cacheKey = `${filename}:${buckets}`;
  if (peakCache.has(cacheKey)) return peakCache.get(cacheKey);

  const peaks = extractPeaks(buffer, buckets);
  peakCache.set(cacheKey, peaks);
  return peaks;
}


// =============================================================
//  SVG rendering
// =============================================================

/**
 * Build polyline points string from peak array.
 */
function peaksToPoints(peaks, x, y, w, h) {
  const midY = y + h / 2;
  const halfH = h / 2;
  const step = w / peaks.length;
  const points = [];

  for (let i = 0; i < peaks.length; i++) {
    const px = x + i * step;
    const py = midY - peaks[i] * halfH;
    points.push(`${px.toFixed(1)},${py.toFixed(1)}`);
  }

  return points.join(" ");
}

/**
 * Resolve the target element for waveform rendering.
 *
 * @param {SVGSVGElement} svg        - the score SVG root
 * @param {string|Element} target    - "self", an element ID string, or an Element
 * @param {Element}        cueElement - the cue element (used when target is "self")
 * @returns {Element|null}
 */
function resolveTarget(svg, target, cueElement) {
  if (target instanceof Element) return target;
  if (target === "self" && cueElement) return cueElement;
  if (typeof target === "string") return svg.getElementById(target);
  return null;
}

/**
 * Render waveform into a target element in the score SVG.
 *
 * @param {SVGSVGElement}  svg        - the score SVG root
 * @param {string|Element} target     - "self", rect ID, or Element
 * @param {AudioBuffer}    buffer     - decoded audio buffer
 * @param {string}         uid        - audio cue uid (for registry)
 * @param {string}         filename   - for peak cache key
 * @param {object}         opts
 * @param {Element}        opts.element  - cue element (for "self" resolution)
 * @param {string}         opts.info     - text label to show at bottom of waveform
 * @returns {object|null}  waveform handle
 */
export function renderWaveform(svg, target, buffer, uid, filename, opts = {}) {
  const targetEl = resolveTarget(svg, target, opts.element);
  if (!targetEl) {
    console.warn(`[waveform] Target not found: ${target}`);
    return null;
  }

  // Guard against elements not yet attached to DOM (e.g. during early WebSocket load)
  // This is expected during initialization - the render will be retried once attached
  if (!targetEl.parentNode) {
    return null;
  }

  // If a waveform already exists for this uid, reuse the group
  const existing = activeWaveforms.get(uid);
  if (existing?.group?.parentNode) {
    // Update peaks if the filename has changed (e.g. audioPool switching files)
    if (filename && existing._filename !== filename) {
      updatePeaks(existing, buffer, filename);
    }
    // Update details label if provided (respect visibility state)
    if (opts.info) {
      if (existing._detailsDiv) {
        existing._detailsDiv.textContent = opts.info;
        // Ensure visibility respects the handle's state
        existing._detailsDiv.style.display = existing._labelVisible ? "" : "none";
      } else if (existing._infoText) {
        existing._infoText.textContent = opts.info;
      }
    }
    // Update color if provided and different (waveform lines and cursor only, not label)
    if (opts.color && opts.color !== existing._color) {
      existing._color = opts.color;
      if (existing.upperLine) existing.upperLine.setAttribute("stroke", opts.color);
      if (existing.lowerLine) existing.lowerLine.setAttribute("stroke", opts.color);
      if (existing.cursor) existing.cursor.setAttribute("stroke", opts.color);
      // Label text stays black for readability
    }
    resetCursor(existing);
    return existing;
  }

  const bbox = targetEl.getBBox();

  // --- Text padding: info text at top, waveform below ---
  const CURSOR_OVERHANG = 0; // SVG units above bbox top (0 = fits exactly)
  const GAP_BELOW_LABEL = 6;  // Gap between label and waveform border
  let textPad = 0;
  let infoText = null;
  let fo = null;

  // Waveform color (default: black)
  const wfColor = opts.color || "#000";
  // Fixed readable font size - doesn't scale with waveform size
  const fontSize = 12;

  // --- Build SVG group ---
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("class", "oscilla-waveform");
  g.setAttribute("pointer-events", "none");
  g.dataset.uid = uid;

  // --- Info text label at top of waveform via foreignObject ---
  // Create and measure the label first to get its actual height
  // Structure: header (always visible) + details (toggleable)
  let detailsDiv = null;
  let headerDiv = null;

  // Determine header and details content
  const hasHeader = opts.header;
  const hasDetails = opts.info;

  if (hasHeader || hasDetails) {
    fo = document.createElementNS(SVG_NS, "foreignObject");
    fo.setAttribute("x", bbox.x);
    fo.setAttribute("y", bbox.y);
    fo.setAttribute("width", bbox.width);
    fo.setAttribute("height", bbox.height); // Temporarily use full height for measurement
    fo.setAttribute("pointer-events", "none");
    fo.style.overflow = "visible";

    // Container div for both header and details
    // Always use black for label text regardless of waveform color
    const containerDiv = document.createElement("div");
    containerDiv.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
    containerDiv.style.cssText = [
      `font-family: monospace`,
      `font-size: ${fontSize}px`,
      `color: #000`,
      "opacity: 0.95",
      "word-break: break-all",
      "overflow-wrap: anywhere",
      "white-space: normal",
      "line-height: 1.2",
      "padding: 1px 2px",
      "box-sizing: border-box",
      `width: ${bbox.width}px`,
      "pointer-events: none"
    ].join(";");

    // Header span (always visible): type + label + filename
    if (hasHeader) {
      headerDiv = document.createElement("span");
      headerDiv.style.cssText = "font-weight: bold; display: inline-block; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; vertical-align: top;";
      headerDiv.textContent = opts.header;
      containerDiv.appendChild(headerDiv);
    }

    // Details div (toggleable): parameters
    if (hasDetails) {
      detailsDiv = document.createElement("div");
      detailsDiv.style.cssText = "opacity: 0.85; margin-top: 2px;";
      detailsDiv.textContent = opts.info;
      // Hide details by default if global visibility is off
      if (!window.waveformLabelsVisible) {
        detailsDiv.style.display = "none";
      }
      containerDiv.appendChild(detailsDiv);
    }

    fo.appendChild(containerDiv);
    g.appendChild(fo);
    infoText = containerDiv;

    // Temporarily add to DOM to measure actual text height
    const parent = targetEl.parentNode;
    if (targetEl.nextSibling) {
      parent.insertBefore(g, targetEl.nextSibling);
    } else {
      parent.appendChild(g);
    }

    // Measure the actual height of the visible content
    const measuredHeight = labelHeight(containerDiv, bbox, fontSize);
    textPad = labelPad(measuredHeight, bbox, GAP_BELOW_LABEL);

    // Update foreignObject height - keep overflow visible so text isn't clipped
    fo.setAttribute("height", measuredHeight + 10); // Buffer for rendering
    fo.style.overflow = "visible";

    // Remove temporarily - will be re-added at the end
    g.remove();
  }

  // The area where peaks are drawn (below text)
  const drawBbox = {
    x: bbox.x,
    y: bbox.y + textPad,
    width: bbox.width,
    height: bbox.height - textPad
  };

  // Horizontal resolution: 1 bucket per SVG unit of width, capped
  const buckets = Math.min(Math.round(drawBbox.width), 2000);
  const peaks   = getPeaks(filename || uid, buffer, buckets);

  // Upper contour (max peaks) -- in draw area below text
  const upperLine = document.createElementNS(SVG_NS, "polyline");
  upperLine.setAttribute("points", peaksToPoints(peaks.maxPeaks, drawBbox.x, drawBbox.y, drawBbox.width, drawBbox.height));
  upperLine.setAttribute("fill", "none");
  upperLine.setAttribute("stroke", wfColor);
  upperLine.setAttribute("stroke-width", "0.8");
  upperLine.setAttribute("stroke-linejoin", "round");
  upperLine.setAttribute("pointer-events", "none");

  // Lower contour (min peaks)
  const lowerLine = document.createElementNS(SVG_NS, "polyline");
  lowerLine.setAttribute("points", peaksToPoints(peaks.minPeaks, drawBbox.x, drawBbox.y, drawBbox.width, drawBbox.height));
  lowerLine.setAttribute("fill", "none");
  lowerLine.setAttribute("stroke", wfColor);
  lowerLine.setAttribute("stroke-width", "0.8");
  lowerLine.setAttribute("stroke-linejoin", "round");
  lowerLine.setAttribute("pointer-events", "none");
  applyVizLine(upperLine, targetEl);     // a box styled by viz()
  applyVizLine(lowerLine, targetEl);

  // Border rect covering the FULL audio object area (including label)
  // This ensures clicks anywhere on the waveform box trigger the audio
  const borderRect = document.createElementNS(SVG_NS, "rect");
  borderRect.setAttribute("x", bbox.x);
  borderRect.setAttribute("y", bbox.y);
  borderRect.setAttribute("width", bbox.width);
  borderRect.setAttribute("height", bbox.height);
  borderRect.setAttribute("fill", "transparent"); // Transparent but still catches pointer events
  // a console box shows its sound, not a frame (viz(uid, border:) adds one)
  borderRect.setAttribute("stroke", targetEl?.dataset?.liveAudio != null ? "none" : "#000");
  borderRect.setAttribute("stroke-width", "1");
  borderRect.setAttribute("pointer-events", "all");
  borderRect.style.cursor = "pointer";

  // Cursor line -- protrudes above bbox, extends full height
  // Hidden initially; shown when playback starts (startCursor)
  const cursor = document.createElementNS(SVG_NS, "line");
  cursor.setAttribute("x1", bbox.x);
  cursor.setAttribute("y1", bbox.y - CURSOR_OVERHANG);
  cursor.setAttribute("x2", bbox.x);
  cursor.setAttribute("y2", bbox.y + bbox.height);
  cursor.setAttribute("stroke", wfColor);
  cursor.setAttribute("stroke-width", "1.6");
  cursor.setAttribute("opacity", "0");
  cursor.setAttribute("pointer-events", "none");

  g.appendChild(upperLine);
  g.appendChild(lowerLine);
  g.appendChild(cursor);
  // Append borderRect AFTER other elements so it's higher in z-order
  // (catches clicks that pass through polylines/cursor which have pointer-events:none)
  g.appendChild(borderRect);

  // Insert waveform group as a SIBLING of the target element.
  // Never as a child -- SVG opacity is multiplicative, so a target with
  // opacity:0 (invisible rect used only for positioning) would hide
  // the waveform too.  MutationObserver mirrors transforms so drag
  // and animation keep them in sync.
  const parent = targetEl.parentNode;
  if (targetEl.nextSibling) {
    parent.insertBefore(g, targetEl.nextSibling);
  } else {
    parent.appendChild(g);
  }

  // Mirror transform changes from the target onto the waveform group
  // so drag translate (and any other transform) keeps them in sync.
  const syncTransform = () => {
    const t = targetEl.getAttribute("transform") || "";
    g.setAttribute("transform", t);
  };
  syncTransform(); // initial sync

  const observer = new MutationObserver(syncTransform);
  observer.observe(targetEl, { attributes: true, attributeFilter: ["transform"] });
  g._transformObserver = observer; // store for cleanup

  // Build handle
  const handle = {
    uid,
    group: g,
    cursor,
    bbox,
    _drawBbox: drawBbox,
    _cursorOverhang: CURSOR_OVERHANG,
    _targetEl: targetEl,
    upperLine,
    lowerLine,
    _infoText: infoText,
    _filename: filename,
    _buffer: buffer,
    _color: wfColor,
    _rafId: null,
    _running: false,
    // Additional refs for dynamic label resizing
    _borderRect: borderRect,
    _fo: fo,
    _fontSize: fontSize,
    _gapBelowLabel: GAP_BELOW_LABEL,
    _peaks: peaks,
    // Label visibility (controls details, header always visible)
    _labelVisible: window.waveformLabelsVisible,
    _detailsDiv: detailsDiv,
    _headerDiv: headerDiv,
  };

  activeWaveforms.set(uid, handle);

  return handle;
}

// =============================================================
//  Dynamic label height adjustment
//
//  When label text changes (e.g. impulse showing current file),
//  call updateWaveformLayout to reposition waveform elements.
// =============================================================

/**
 * Update waveform layout after label text changes.
 * Re-measures label height and repositions waveform elements.
 */
export function updateWaveformLayout(handle) {
  if (!handle?.bbox) return;
  const bbox = handle.bbox;
  let textPad = 0;
  if (handle._infoText && handle._fo) {
    const height = labelHeight(handle._infoText, bbox, handle._fontSize || 14);
    textPad = labelPad(height, bbox, handle._gapBelowLabel || 6);
    handle._fo.setAttribute("height", height + 10);
  }
  // in place: what holds the handle's draw area keeps seeing it
  handle._drawBbox = Object.assign(handle._drawBbox || {}, { x: bbox.x, y: bbox.y + textPad, width: bbox.width, height: bbox.height - textPad });

  // the border covers the FULL bbox, for clicks on the label area too
  if (handle._borderRect) {
    handle._borderRect.setAttribute("y", bbox.y);
    handle._borderRect.setAttribute("height", bbox.height);
  }
  drawPeaks(handle, handle._drawBbox);
}

// =============================================================
//  Multi-cursor support
//
//  addCursor / removeCursor create independent cursor lines
//  within an existing waveform group.  Each sub-cursor gets its
//  own RAF loop and can be started / reset with the standard
//  startCursor / resetCursor functions.
//
//  Impulse cues use this to show one cursor per polyphonic hit.
// =============================================================

/**
 * Add a named sub-cursor to an existing waveform handle.
 *
 * @param {object} handle    - parent waveform handle from renderWaveform
 * @param {string} cursorId  - unique id for this cursor (e.g. playUid)
 * @param {object} opts      - { color, width, opacity }
 * @returns {object} sub-handle compatible with startCursor / resetCursor
 */
export function addCursor(handle, cursorId, opts = {}) {
  if (!handle?.group || !handle?.bbox) return null;

  const overhang = handle._cursorOverhang || 0;

  const cursor = document.createElementNS(SVG_NS, "line");
  cursor.setAttribute("x1", handle.bbox.x);
  cursor.setAttribute("y1", handle.bbox.y - overhang);
  cursor.setAttribute("x2", handle.bbox.x);
  cursor.setAttribute("y2", handle.bbox.y + handle.bbox.height);
  cursor.setAttribute("stroke", opts.color || "#c00");
  cursor.setAttribute("stroke-width", opts.width || "1.6");
  cursor.setAttribute("opacity", opts.opacity || "0.45");
  cursor.setAttribute("pointer-events", "none");
  cursor.dataset.cursorId = cursorId;

  handle.group.appendChild(cursor);

  const subHandle = {
    uid: cursorId,
    cursor,
    bbox: handle.bbox,
    _rafId: null,
    _running: false,
    _parentHandle: handle,
  };

  if (!handle._cursors) handle._cursors = new Map();
  handle._cursors.set(cursorId, subHandle);

  return subHandle;
}

/**
 * Remove a named sub-cursor from a waveform handle.
 */
export function removeCursor(handle, cursorId) {
  if (!handle?._cursors) return;

  const sub = handle._cursors.get(cursorId);
  if (!sub) return;

  resetCursor(sub);
  sub.cursor?.remove();
  handle._cursors.delete(cursorId);
}

/**
 * Remove all sub-cursors from a waveform handle.
 */
export function removeAllCursors(handle) {
  if (!handle?._cursors) return;

  for (const [, sub] of handle._cursors) {
    resetCursor(sub);
    sub.cursor?.remove();
  }
  handle._cursors.clear();
}


/**
 * Update the peak polylines of an existing waveform to show a different file.
 * Used by audioPool when switching between pool files.
 *
 * @param {object} handle   - waveform handle
 * @param {AudioBuffer} buffer - new audio buffer
 * @param {string} filename  - new filename (for peak cache key)
 */
export function updatePeaks(handle, buffer, filename) {
  if (!handle?.upperLine || !handle?.lowerLine || !handle?.bbox) return;

  const drawArea = handle._drawBbox || handle.bbox;
  const buckets = Math.min(Math.round(drawArea.width), 2000);
  const peaks = getPeaks(filename, buffer, buckets);

  handle.upperLine.setAttribute("points",
    peaksToPoints(peaks.maxPeaks, drawArea.x, drawArea.y, drawArea.width, drawArea.height));
  handle.lowerLine.setAttribute("points",
    peaksToPoints(peaks.minPeaks, drawArea.x, drawArea.y, drawArea.width, drawArea.height));

  handle._filename = filename;
}


/**
 * Resize a waveform in place: recalculate all polyline points,
 * reposition cursors, and update info text for the new target
 * dimensions.  Non-destructive -- peak layers and cursors survive.
 */
export function resizeWaveform(handle) {
  if (!handle?._targetEl) return;

  const el = handle._targetEl;
  const newBbox = el.getBBox();

  // Update stored bbox refs (sub-cursors hold reference to handle.bbox)
  handle.bbox.x = newBbox.x;
  handle.bbox.y = newBbox.y;
  handle.bbox.width = newBbox.width;
  handle.bbox.height = newBbox.height;

  // the label: its new place, width and size, then measured as every
  // other layout measures it (updateWaveformLayout), and the peaks under it
  if (handle._infoText) {
    const fontSize = Math.max(10, Math.min(newBbox.height * 0.07, 20));
    const fo = handle._infoText.parentNode;
    if (fo && fo.tagName === "foreignObject") {
      fo.setAttribute("x", newBbox.x);
      fo.setAttribute("y", newBbox.y);
      fo.setAttribute("width", newBbox.width);
      handle._infoText.style.width = `${newBbox.width}px`;
      handle._infoText.style.fontSize = `${fontSize}px`;
    }
    handle._fontSize = fontSize;
  }
  updateWaveformLayout(handle);

  // --- Reposition main cursor ---
  const overhang = handle._cursorOverhang || 0;
  if (handle.cursor) {
    handle.cursor.setAttribute("x1", newBbox.x);
    handle.cursor.setAttribute("y1", newBbox.y - overhang);
    handle.cursor.setAttribute("x2", newBbox.x);
    handle.cursor.setAttribute("y2", newBbox.y + newBbox.height);
  }

  // --- Reposition sub-cursors ---
  if (handle._cursors) {
    for (const [, sub] of handle._cursors) {
      if (sub.cursor) {
        sub.cursor.setAttribute("y1", newBbox.y - overhang);
        sub.cursor.setAttribute("y2", newBbox.y + newBbox.height);
        // Reset x to left edge if not currently animating
        if (!sub._running) {
          sub.cursor.setAttribute("x1", newBbox.x);
          sub.cursor.setAttribute("x2", newBbox.x);
        }
      }
    }
  }
}


/**
 * Resize ALL waveform handles attached to a given SVG element.
 * Used by edge-resize handlers on live rects to ensure all
 * routed waveforms (e.g. waveform:uid) resize together.
 */
export function resizeWaveformsForElement(targetEl) {
  for (const [, handle] of activeWaveforms) {
    if (handle._targetEl === targetEl) {
      if (handle._scopeLine) {
        // Scope handles need destroy+rebuild (handled by caller if needed)
        continue;
      }
      resizeWaveform(handle);
    }
  }
}


// =============================================================
//  Peak layers -- stacked waveform contours for multi-file display
//
//  Each layer is a pair of polylines (upper/lower peak envelope)
//  rendered in a distinct color.  Used by audioImpulse to show
//  which pool files are active.
// =============================================================

/** Color palette for peak layers */
const LAYER_COLORS = [
  "#c00", "#07a", "#e80", "#2a7", "#80c", "#c55",
  "#089", "#a60", "#5a3", "#a3c", "#c80", "#369"
];

let layerColorIndex = 0;

/**
 * Add a peak layer (upper+lower contour) for a specific file.
 *
 * @param {object} handle    - parent waveform handle from renderWaveform
 * @param {AudioBuffer} buffer - decoded audio buffer
 * @param {string} filename  - for peak cache key and dedup
 * @param {object} opts      - { color, opacity, id }
 * @returns {string} layerId (the filename, or opts.id)
 */
export function addPeakLayer(handle, buffer, filename, opts = {}) {
  if (!handle?.group || !handle?.bbox) return null;

  if (!handle._peakLayers) handle._peakLayers = new Map();

  const layerId = opts.id || filename;

  // Avoid duplicate layers for same file
  if (handle._peakLayers.has(layerId)) return layerId;

  const drawArea = handle._drawBbox || handle.bbox;
  const buckets = Math.min(Math.round(drawArea.width), 2000);
  const peaks = getPeaks(filename, buffer, buckets);

  const color = opts.color || LAYER_COLORS[layerColorIndex++ % LAYER_COLORS.length];
  const opacity = opts.opacity || "1";

  const upper = document.createElementNS(SVG_NS, "polyline");
  upper.setAttribute("points", peaksToPoints(peaks.maxPeaks, drawArea.x, drawArea.y, drawArea.width, drawArea.height));
  upper.setAttribute("fill", "none");
  upper.setAttribute("stroke", color);
  upper.setAttribute("stroke-width", "0.6");
  upper.setAttribute("stroke-linejoin", "round");
  upper.setAttribute("opacity", opacity);
  upper.setAttribute("pointer-events", "none");

  const lower = document.createElementNS(SVG_NS, "polyline");
  lower.setAttribute("points", peaksToPoints(peaks.minPeaks, drawArea.x, drawArea.y, drawArea.width, drawArea.height));
  lower.setAttribute("fill", "none");
  lower.setAttribute("stroke", color);
  lower.setAttribute("stroke-width", "0.6");
  lower.setAttribute("stroke-linejoin", "round");
  lower.setAttribute("opacity", opacity);
  lower.setAttribute("pointer-events", "none");

  // Insert before cursors (cursors should stay on top)
  const firstCursor = handle.group.querySelector("line");
  if (firstCursor) {
    handle.group.insertBefore(upper, firstCursor);
    handle.group.insertBefore(lower, firstCursor);
  } else {
    handle.group.appendChild(upper);
    handle.group.appendChild(lower);
  }

  handle._peakLayers.set(layerId, { upper, lower, color, buffer, filename });

  return layerId;
}

/**
 * Remove a peak layer by id.
 */
export function removePeakLayer(handle, layerId) {
  if (!handle?._peakLayers) return;

  const layer = handle._peakLayers.get(layerId);
  if (!layer) return;

  layer.upper?.remove();
  layer.lower?.remove();
  handle._peakLayers.delete(layerId);
}

/**
 * Remove all peak layers from a waveform handle.
 */
export function removeAllPeakLayers(handle) {
  if (!handle?._peakLayers) return;

  for (const [, layer] of handle._peakLayers) {
    layer.upper?.remove();
    layer.lower?.remove();
  }
  handle._peakLayers.clear();
}


// =============================================================
//  Cursor control
// =============================================================

/** A sound's cursor forgotten (its last voice stopped): the next prime does not start it again. */
export function clearCursorParams(uid) {
  _cursorRestoreParams.delete(uid);
}

export function getCursorParams(uid) {
  return _cursorRestoreParams.get(uid);
}

/**
 * Start the playback cursor animation.
 * Supports reverse playback (negative speed): cursor moves right-to-left.
 * @param {object} handle - waveform handle
 * @param {AudioContext} audioCtx - audio context for timing
 * @param {number} startTime - audioCtx.currentTime when playback started
 * @param {number} duration - duration of audio in seconds
 * @param {number} speed - playback speed (negative = reverse)
 * @param {number} loop - loop count (0 = infinite, 1 = no loop, >1 = N loops)
 * @param {object} range - { from, to } as fractions of the box width: WHERE in
 *   the file the current play sits (in:/seg:/out: as fractions of the
 *   buffer). The cursor then sweeps only that part of the waveform, so jumps
 *   between start points are visible. Omitted: the whole box.
 */
export function startCursor(handle, audioCtx, startTime, duration, speed = 1, loop = 1, range = null) {
  if (!handle?.cursor) return;

  // Persist params so a re-render (audioFile.js primeWaveform) can restart it
  for (const [uid, h] of activeWaveforms) {
    if (h === handle) {
      _cursorRestoreParams.set(uid, { audioCtx, startTime, duration, speed, loop, range });
      break;
    }
  }

  handle._running = true;
  const absSpeed = Math.abs(speed) || 1;
  const reverse = speed < 0;
  const effectiveDuration = duration / absSpeed;
  const { bbox, cursor } = handle;
  const isLooping = loop === 0 || loop > 1;
  const r0 = Math.max(0, Math.min(1, Number(range?.from) || 0));
  const r1 = Math.max(r0, Math.min(1, Number.isFinite(Number(range?.to)) && range?.to != null ? Number(range.to) : 1));
  const rw = r1 - r0;

  // Show cursor when playback starts
  cursor.setAttribute("opacity", "0.6");

  function tick() {
    if (!handle._running) return;

    const elapsed = audioCtx.currentTime - startTime;
    let progress;

    if (isLooping) {
      // For looping audio, wrap progress around
      progress = (elapsed / effectiveDuration) % 1;
    } else {
      progress = Math.min(elapsed / effectiveDuration, 1);
    }

    const cx = reverse
      ? bbox.x + bbox.width * (r1 - progress * rw)   // right-to-left within the range
      : bbox.x + bbox.width * (r0 + progress * rw);  // left-to-right within the range

    cursor.setAttribute("x1", cx);
    cursor.setAttribute("x2", cx);

    if (!isLooping && progress >= 1) {
      // Non-looping: stop when done
      // Don't auto-reset - let the audio onended handler manage cursor reset.
      handle._running = false;
      return;
    }

    handle._rafId = requestAnimationFrame(tick);
  }

  if (handle._rafId) cancelAnimationFrame(handle._rafId);
  handle._rafId = requestAnimationFrame(tick);
}

/**
 * Reset cursor to start position.
 */
export function resetCursor(handle) {
  if (!handle) return;

  handle._running = false;
  if (handle._rafId) {
    cancelAnimationFrame(handle._rafId);
    handle._rafId = null;
  }

  // Disable drag if active
  disableCursorDrag(handle);

  if (handle.cursor && handle.bbox) {
    handle.cursor.setAttribute("x1", handle.bbox.x);
    handle.cursor.setAttribute("x2", handle.bbox.x);
    handle.cursor.setAttribute("opacity", "0"); // Hide when not playing
  }
}

/**
 * Enable cursor dragging (for paused audio scrubbing).
 * @param {object} handle - waveform handle
 * @param {function} onDrag - callback(progress) where progress is 0-1
 */
export function enableCursorDrag(handle, onDrag) {
  if (!handle?.cursor || !handle?.bbox) return;

  const cursor = handle.cursor;
  const bbox = handle.bbox;
  const svg = cursor.ownerSVGElement;
  if (!svg) return;

  // Create a wide invisible hit zone rect for easier clicking
  const HIT_ZONE_WIDTH = 20;
  const hitZone = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  const cursorX = parseFloat(cursor.getAttribute("x1")) || bbox.x;
  hitZone.setAttribute("x", cursorX - HIT_ZONE_WIDTH / 2);
  hitZone.setAttribute("y", bbox.y - 10);
  hitZone.setAttribute("width", HIT_ZONE_WIDTH);
  hitZone.setAttribute("height", bbox.height + 20);
  hitZone.setAttribute("fill", "transparent");
  hitZone.setAttribute("cursor", "ew-resize");
  hitZone.setAttribute("pointer-events", "all");
  hitZone.classList.add("waveform-cursor-draggable");

  // in the waveform's group, last: on top of the waveform, and moved with
  // the box (the group carries a dragged box's translate)
  const host = handle.group ?? svg;
  host.appendChild(hitZone);

  let dragging = false;

  // Track last valid progress to avoid jumps when CTM is unavailable
  let lastProgress = null;

  const getProgress = (clientX) => {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    // into the group's units, where bbox and the cursor are
    const ctm = host.getScreenCTM()?.inverse();
    if (!ctm) {
      // CTM not available - return last known progress or null to skip update
      return lastProgress;
    }
    const svgPt = pt.matrixTransform(ctm);
    const progress = Math.max(0, Math.min(1, (svgPt.x - bbox.x) / bbox.width));
    lastProgress = progress;
    return progress;
  };

  const updateCursor = (progress) => {
    const cx = bbox.x + progress * bbox.width;
    cursor.setAttribute("x1", cx);
    cursor.setAttribute("x2", cx);
    hitZone.setAttribute("x", cx - HIT_ZONE_WIDTH / 2);
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragging = true;
    hitZone.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const progress = getProgress(e.clientX);
    if (progress === null) return; // Skip if CTM not ready
    updateCursor(progress);
    onDrag?.(progress);
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    dragging = false;
    hitZone.releasePointerCapture(e.pointerId);
  };

  hitZone.addEventListener("pointerdown", onPointerDown);
  hitZone.addEventListener("pointermove", onPointerMove);
  hitZone.addEventListener("pointerup", onPointerUp);
  hitZone.addEventListener("pointercancel", onPointerUp);

  // Store cleanup function and hit zone reference
  handle._cursorHitZone = hitZone;
  handle._cursorDragCleanup = () => {
    hitZone.removeEventListener("pointerdown", onPointerDown);
    hitZone.removeEventListener("pointermove", onPointerMove);
    hitZone.removeEventListener("pointerup", onPointerUp);
    hitZone.removeEventListener("pointercancel", onPointerUp);
    hitZone.remove();
    handle._cursorHitZone = null;
  };
}

/**
 * Disable cursor dragging.
 */
export function disableCursorDrag(handle) {
  if (!handle) return;
  handle._cursorDragCleanup?.();
  handle._cursorDragCleanup = null;
}

/**
 * Remove waveform SVG group entirely.
 */
export function destroyWaveform(uid) {
  const handle = activeWaveforms.get(uid);
  if (!handle) return;

  resetCursor(handle);
  // its scope loop with it (scope.js stopScope; scope.js imports this file):
  // a file's or the input's scope kept drawing into the removed box
  handle._scopeRunning = false;
  if (handle._scopeRafId) cancelAnimationFrame(handle._scopeRafId);
  handle._scopeRafId = null;
  handle._cleanupTooltip?.();  // Cleanup scope tooltip if present
  handle.group?._transformObserver?.disconnect();
  handle.group?.remove();
  // drawn outside its group (a turning box's trail, painted on the stage)
  for (const n of handle._detached || []) n.remove();
  activeWaveforms.delete(uid);
}

/**
 * Get an active waveform handle by uid.
 */
export function getWaveform(uid) {
  return activeWaveforms.get(uid) || null;
}

/** Every waveform or scope handle drawn into this element. */
export function waveformsForElement(el) {
  const results = [];
  for (const [, handle] of activeWaveforms) {
    if (handle._targetEl === el) results.push(handle);
  }
  return results;
}

/**
 * Find all waveform handles whose uid starts with the given prefix.
 * Useful for finding all poly sub-voice scopes for an audio object.
 */
export function getWaveformsByPrefix(prefix) {
  const results = [];
  for (const [uid, handle] of activeWaveforms) {
    if (uid.startsWith(prefix)) results.push(handle);
  }
  return results;
}


// =============================================================
//  Shared helpers for sibling modules (e.g. scope.js)
// =============================================================

export const SVG_NS_EXPORT = SVG_NS;

/**
 * Resolve the target element for rendering.
 * Re-exported so scope.js can share the same resolution logic.
 */
export { resolveTarget };

/**
 * Register a handle in the shared waveform registry.
 * Used by scope.js to store scope handles alongside waveform handles.
 */
export function registerHandle(uid, handle) {
  activeWaveforms.set(uid, handle);
}


